import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, ClipboardCheck, Ruler, FileCheck2, Sparkles, Award, ShieldCheck } from 'lucide-react';
const standards = [{
  icon: ClipboardCheck,
  title: 'On-site assessment',
  body: 'Every recommendation starts at your property, not from a phone script or an online estimate.'
}, {
  icon: Ruler,
  title: 'Correct system sizing',
  body: 'We consider room size, insulation, orientation, ceiling height and how each space is used.'
}, {
  icon: FileCheck2,
  title: 'Fixed scope in writing',
  body: 'Brand, model, installation scope, electrical work and removal are clearly documented before work starts.'
}, {
  icon: Sparkles,
  title: 'Clean installation',
  body: 'Drop sheets, tidy work areas, careful commissioning and a complete handover before we leave.'
}];
export function InstallationStandard() {
  return <section id="installation-standard" className="bg-white py-20 md:py-28 scroll-mt-24" data-id="element-375" data-mp="1ma5sm8hayzh8">
      <div className="max-w-6xl mx-auto px-6" data-id="element-376" data-mp="18t4j7mfwpf3h">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start" data-id="element-377" data-mp="13sq81vj0dp18">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55
        }} className="lg:col-span-5" data-id="element-378" data-mp="13uks77mghd49">
            <div className="rounded-3xl bg-brand text-white p-8 md:p-10 shadow-[0_24px_50px_-18px_rgba(2,110,183,0.35)]" data-id="element-379" data-mp="1riatcl2mwydo">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-wider mb-8" data-id="element-380" data-mp="1ngzbx2twzjgk">
                <Award size={14} data-id="element-381" data-mp="1dqw96awzqr51" /> Established 1995
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight" data-id="element-382" data-mp="1dojuq7qfyywq">
                The Langler installation standard.
              </h2>
              <p className="mt-5 text-base md:text-lg leading-relaxed text-white/85" data-id="element-383" data-mp="26ea7odgc7ur9">
                Reliable comfort starts with a proper assessment, a correctly sized system and an installation completed with care.
              </p>
              <div className="mt-8 pt-7 border-t border-white/15 grid grid-cols-2 gap-5" data-id="element-384" data-mp="2ucrcx9yvpmir">
                <div data-id="element-385" data-mp="2gj07aco9qmsb">
                  <ShieldCheck className="w-6 h-6 text-accent mb-2" data-id="element-386" data-mp="2kku3a7nir2m5" />
                  <div className="font-bold" data-id="element-387" data-mp="2kj0v2nx9cac1">Workmanship guarantee</div>
                </div>
                <div data-id="element-388" data-mp="26jhow0o0b92d">
                  <Award className="w-6 h-6 text-accent mb-2" data-id="element-389" data-mp="26nf8enehy2ms" />
                  <div className="font-bold" data-id="element-390" data-mp="2bkyl3ivjq7cm">Diamond Dealer</div>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-7" data-id="element-391" data-mp="21lznqwg8dy4x">
            <div className="kicker mb-3" data-id="element-392" data-mp="21n0y0ypfagll">How we protect your investment</div>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight text-ink-900" data-id="element-393" data-mp="3rxvuwo4w7yl0">
              Designed for your property. Installed properly.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-stone-600 max-w-2xl" data-id="element-394" data-mp="1kcncsemjfd5x">
              An over-sized or under-sized system can cost more to run and wear out earlier. Our process removes guesswork before installation begins.
            </p>

            <div className="grid sm:grid-cols-2 gap-5 mt-9" data-id="element-395" data-mp="1u38i0kwpvfxt">
              {standards.map((item, index) => {
              const Icon = item.icon;
              return <motion.div key={item.title} initial={{
                opacity: 0,
                y: 14
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: index * 0.06
              }} className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm" data-id="element-396" data-mp="23zv1b59e9yxq" data-mpi={index}>
                    <div className="w-11 h-11 rounded-xl bg-brand-soft text-brand flex items-center justify-center mb-4" data-id="element-397" data-mp="iocsu50g1w30">
                      <Icon size={21} data-id="element-398" data-mp="dprts8wxhig5" />
                    </div>
                    <h3 className="text-lg font-bold text-ink-900 mb-2" data-id="element-399" data-mp="g3n6kfi9pf7w">{item.title}</h3>
                    <p className="text-sm leading-relaxed text-stone-600" data-id="element-400" data-mp="k3xnuobifs22">{item.body}</p>
                  </motion.div>;
            })}
            </div>

            <div className="mt-9 flex flex-col sm:flex-row gap-3" data-id="element-401" data-mp="p1qshcx60e08">
              <a href="#quote-form" className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-xl bg-accent hover:bg-accent-600 text-ink-900 font-bold transition-all hover:scale-[1.02] shadow-cta" data-id="element-402" data-mp="u24qe251y7kj">
                Book my free assessment
                <ArrowRight size={16} strokeWidth={2.5} data-id="element-403" data-mp="6bhhpenbbdwx" />
              </a>
              <a href="tel:+61864043888" className="inline-flex items-center justify-center gap-2 h-14 px-6 rounded-xl border-2 border-brand text-brand hover:bg-brand-soft font-semibold transition-all" data-id="element-404" data-mp="b42b4f2b3ozx">
                <Phone size={16} strokeWidth={2.5} data-id="element-405" data-mp="3eqj12e8eejll" />
                Call Langler Air
              </a>
            </div>
            <p className="mt-4 text-xs text-stone-500" data-id="element-406" data-mp="32ajcs0ztpvcp">Free quote. No obligation. No spam. No sales tactics.</p>
          </div>
        </div>
      </div>
    </section>;
}